import React, { useState } from "react";
import SubPageLayout from "../../components/shared/SubPageLayout";

const CERequirements = () => {
  const breadcrumbItems = [
    { label: "Education & Licensing", link: "/education-licensing" },
    { label: "CE Requirements", link: "/education-licensing/ce-requirements" },
  ];

  // Mock data for CE credits and requirements
  const [ceCredits] = useState({
    total: {
      required: 30,
      completed: 18,
      remaining: 12,
      deadline: "2025-12-31",
    },
    byCategory: [
      {
        category: "Ethics",
        required: 6,
        completed: 4,
        remaining: 2,
      },
      {
        category: "Investment Products",
        required: 8,
        completed: 5,
        remaining: 3,
      },
      {
        category: "Retirement Planning",
        required: 8,
        completed: 4,
        remaining: 4,
      },
      {
        category: "Tax Planning",
        required: 8,
        completed: 5,
        remaining: 3,
      },
    ],
  });

  // Mock data for recommended courses
  const [recommendedCourses] = useState([
    {
      id: 1,
      title: "Ethics in Financial Planning",
      provider: "RegEd",
      credits: 3,
      format: "Online",
      duration: "3 hours",
      price: "$99",
      rating: 4.5,
      category: "Ethics",
      relevance: "High",
      deadline: "2025-06-30",
    },
    {
      id: 2,
      title: "Advanced Retirement Strategies",
      provider: "Kaplan",
      credits: 4,
      format: "Webinar",
      duration: "4 hours",
      price: "$149",
      rating: 4.8,
      category: "Retirement Planning",
      relevance: "High",
      deadline: "2025-06-30",
    },
    {
      id: 3,
      title: "Tax Planning for High Net Worth Clients",
      provider: "WebCE",
      credits: 3,
      format: "Self-paced",
      duration: "3 hours",
      price: "$129",
      rating: 4.3,
      category: "Tax Planning",
      relevance: "Medium",
      deadline: "2025-09-30",
    },
  ]);

  // Mock data for marketplace filters
  const [filters, setFilters] = useState({
    category: "all",
    provider: "all",
    format: "all",
    priceRange: "all",
  });

  const [activeTab, setActiveTab] = useState("overview");

  const getProgressColor = (completed, required) => {
    const percentage = (completed / required) * 100;
    if (percentage >= 75) return "bg-green-600";
    if (percentage >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
            Total CE Credits Required
          </h3>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
            {ceCredits.total.required}
          </p>
          <div className="mt-4">
            <div className="text-sm text-gray-500 dark:text-gray-500">
              Due by {formatDate(ceCredits.total.deadline)}
            </div>
          </div>
        </div>

        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
            Credits Completed
          </h3>
          <p className="mt-2 text-3xl font-bold text-green-600">
            {ceCredits.total.completed}
          </p>
          <div className="mt-4">
            <div className="text-sm text-gray-500 dark:text-gray-500">
              {(
                (ceCredits.total.completed / ceCredits.total.required) *
                100
              ).toFixed(0)}
              % Complete
            </div>
          </div>
        </div>

        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
            Credits Remaining
          </h3>
          <p className="mt-2 text-3xl font-bold text-red-600">
            {ceCredits.total.remaining}
          </p>
          <div className="mt-4">
            <div className="text-sm text-gray-500 dark:text-gray-500">
              {(
                (ceCredits.total.remaining / ceCredits.total.required) *
                100
              ).toFixed(0)}
              % Remaining
            </div>
          </div>
        </div>

        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500">
            Days Until Deadline
          </h3>
          <p className="mt-2 text-3xl font-bold text-blue-600">
            {Math.ceil(
              (new Date(ceCredits.total.deadline) - new Date()) /
                (1000 * 60 * 60 * 24),
            )}
          </p>
          <div className="mt-4">
            <div className="text-sm text-gray-500 dark:text-gray-500">
              Deadline: {formatDate(ceCredits.total.deadline)}
            </div>
          </div>
        </div>
      </div>

      {/* Progress by Category */}
      <div className="bg-surface-default dark:bg-surface-default shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Progress by Category
          </h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {ceCredits.byCategory.map((category, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {category.category}
                  </h3>
                  <span className="text-sm text-gray-500 dark:text-gray-500">
                    {category.completed}/{category.required} Credits
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full ${getProgressColor(category.completed, category.required)}`}
                    style={{
                      width: `${(category.completed / category.required) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recommended Courses */}
      <div className="bg-surface-default dark:bg-surface-default shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Recommended Courses
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {recommendedCourses.map((course) => (
              <div key={course.id} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {course.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                      {course.provider}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      course.relevance === "High"
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {course.relevance} Priority
                  </span>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-500">Credits:</span>
                    <span className="text-gray-900 dark:text-gray-100">{course.credits}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-500">Format:</span>
                    <span className="text-gray-900 dark:text-gray-100">{course.format}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-500">Duration:</span>
                    <span className="text-gray-900 dark:text-gray-100">{course.duration}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-500">Price:</span>
                    <span className="text-gray-900 dark:text-gray-100">{course.price}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <button className="w-full bg-[#202EC6] text-white px-4 py-2 rounded-lg hover:bg-opacity-90">
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderMarketplaceTab = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            value={filters.category}
            onChange={(e) =>
              setFilters({ ...filters, category: e.target.value })
            }
            className="rounded-lg border-gray-300 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Categories</option>
            <option value="ethics">Ethics</option>
            <option value="investment">Investment Products</option>
            <option value="retirement">Retirement Planning</option>
            <option value="tax">Tax Planning</option>
          </select>

          <select
            value={filters.provider}
            onChange={(e) =>
              setFilters({ ...filters, provider: e.target.value })
            }
            className="rounded-lg border-gray-300 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Providers</option>
            <option value="reged">RegEd</option>
            <option value="kaplan">Kaplan</option>
            <option value="webce">WebCE</option>
            <option value="betterCE">BetterCE</option>
          </select>

          <select
            value={filters.format}
            onChange={(e) => setFilters({ ...filters, format: e.target.value })}
            className="rounded-lg border-gray-300 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Formats</option>
            <option value="online">Online</option>
            <option value="webinar">Webinar</option>
            <option value="self-paced">Self-paced</option>
          </select>

          <select
            value={filters.priceRange}
            onChange={(e) =>
              setFilters({ ...filters, priceRange: e.target.value })
            }
            className="rounded-lg border-gray-300 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Prices</option>
            <option value="0-50">$0 - $50</option>
            <option value="51-100">$51 - $100</option>
            <option value="101-200">$101 - $200</option>
            <option value="201+">$201+</option>
          </select>
        </div>
      </div>

      {/* Course Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {recommendedCourses.map((course) => (
          <div key={course.id} className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {course.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">{course.provider}</p>
              </div>
              <div className="flex items-center">
                <span className="text-yellow-400 mr-1">★</span>
                <span className="text-sm text-gray-900 dark:text-gray-100">{course.rating}</span>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-500">Credits:</span>
                <span className="text-gray-900 dark:text-gray-100">{course.credits}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-500">Format:</span>
                <span className="text-gray-900 dark:text-gray-100">{course.format}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-500">Duration:</span>
                <span className="text-gray-900 dark:text-gray-100">{course.duration}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-500">Category:</span>
                <span className="text-gray-900 dark:text-gray-100">{course.category}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-500">Price:</span>
                <span className="text-gray-900 dark:text-gray-100">{course.price}</span>
              </div>
            </div>
            <div className="mt-6 flex space-x-4">
              <button className="flex-1 bg-gray-100 text-gray-900 dark:text-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200">
                Learn More
              </button>
              <button className="flex-1 bg-[#202EC6] text-white px-4 py-2 rounded-lg hover:bg-opacity-90">
                Enroll
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <SubPageLayout
      title="CE Requirements"
      subtitle="Track and manage your continuing education requirements"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="space-y-6">
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("overview")}
              className={`${
                activeTab === "overview"
                  ? "border-[#202EC6] text-[#202EC6]"
                  : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("marketplace")}
              className={`${
                activeTab === "marketplace"
                  ? "border-[#202EC6] text-[#202EC6]"
                  : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              CE Marketplace
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "overview"
          ? renderOverviewTab()
          : renderMarketplaceTab()}
      </div>
    </SubPageLayout>
  );
};

export default CERequirements;
